import { useEffect, lazy, Suspense } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Loader2 } from 'lucide-react';
import { NavigationBar } from '@/components/landing/NavigationBar';
import { ScrollProgressBar } from '@/components/landing/ScrollProgressBar';
import { HeroSection } from '@/components/landing/HeroSection';
import { SEOHead } from '@/components/seo/SEOHead';

import { WorkspacePeekSection } from '@/components/landing/WorkspacePeekSection';
import { UniversityLogoStrip } from '@/components/landing/UniversityLogoStrip';
import { WhyVerithosSection } from '@/components/landing/WhyVerithosSection';

// Lazy-load below-the-fold sections to reduce initial JS
const BenefitsSection = lazy(() => import('@/components/landing/BenefitsSection').then(m => ({ default: m.BenefitsSection })));
const DemoProofStrip = lazy(() => import('@/components/landing/DemoProofStrip').then(m => ({ default: m.DemoProofStrip })));
const TestimonialsSection = lazy(() => import('@/components/landing/TestimonialsSection').then(m => ({ default: m.TestimonialsSection })));
const WhoIsItForSection = lazy(() => import('@/components/landing/WhoIsItForSection').then(m => ({ default: m.WhoIsItForSection })));
const TrustSecuritySection = lazy(() => import('@/components/landing/TrustSecuritySection').then(m => ({ default: m.TrustSecuritySection })));
const AskAISection = lazy(() => import('@/components/landing/AskAISection').then(m => ({ default: m.AskAISection })));
const HowItWorksSection = lazy(() => import('@/components/landing/HowItWorksSection').then(m => ({ default: m.HowItWorksSection })));
const CTASection = lazy(() => import('@/components/landing/CTASection').then(m => ({ default: m.CTASection })));
const FooterSection = lazy(() => import('@/components/landing/FooterSection').then(m => ({ default: m.FooterSection })));

export default function Index() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  // Disable browser scroll restoration & force top on mount
  useEffect(() => {
    if ('scrollRestoration' in history) {
      history.scrollRestoration = 'manual';
    }
    window.scrollTo(0, 0);
  }, []);

  // Also scroll to top when loading finishes (content swap from loader → page)
  useEffect(() => {
    if (!loading) {
      window.scrollTo(0, 0);
    }
  }, [loading]);

  useEffect(() => {
    if (!loading && user) {
      navigate('/projects', { replace: true });
    }
  }, [user, loading, navigate]);

  // Handle scroll-to state from navigation
  useEffect(() => {
    const state = location.state as { scrollTo?: string } | null;
    if (state?.scrollTo) {
      setTimeout(() => {
        document.getElementById(state.scrollTo!)?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background relative">
      {/* Research texture — subtle dot grid */}
      <div 
        aria-hidden="true"
        className="fixed inset-0 pointer-events-none z-0"
        style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, hsl(var(--foreground) / 0.055) 0.5px, transparent 0)',
          backgroundSize: '22px 22px',
        }}
      />
      <SEOHead />
      <ScrollProgressBar />
      <NavigationBar />
      <main>
        <HeroSection />
        <UniversityLogoStrip />
        <WhyVerithosSection />
        <WorkspacePeekSection />
        <Suspense fallback={null}>
          <HowItWorksSection />
          <DemoProofStrip />
          <BenefitsSection />
          <WhoIsItForSection />
          <TestimonialsSection />
          <TrustSecuritySection />
          <AskAISection />
          <CTASection />
          <FooterSection />
        </Suspense>
      </main>
    </div>
  );
}
